/**
 * SP-5 capacity meters + the approach warning (AD-775, docs/systems/
 * inventory-capacity.md §8). Pure helpers over the snapshot's `capacity` field
 * so the panel and the store say the same thing; the server decides `warn`
 * (used/cap >= warnFraction), this module only words it and paces it.
 *
 * Pacing ("warn once per session and on each further rise"): a cap warns when it
 * first enters the server's `warn` list this session, and once more when it
 * reaches full — two toasts at most per cap per session, so a near-full sack
 * does not toast on every gather. Leaving the warn list re-arms both.
 */

import type { CapacityKey, InventorySnapshotPayload } from '@great-cloud/shared/client';

type Capacity = NonNullable<InventorySnapshotPayload['capacity']>;
type Held = NonNullable<Capacity['held']>;

/** Display order and the owner's names for the five caps (§4). */
export const CAPACITY_LABELS: readonly { key: CapacityKey; label: string; unit: string }[] = [
  { key: 'purse', label: 'Coin purse', unit: 'Shards' },
  { key: 'sack', label: 'Sack', unit: 'materials' },
  { key: 'pack', label: 'Pack', unit: 'items' },
  { key: 'chest', label: 'Chest', unit: 'items' },
  { key: 'account', label: 'All holdings', unit: 'items' },
];

/** Per-cap warning state for this session: the stage reached (1 = near, 2 = full). */
export type CapacityWarnState = Partial<Record<CapacityKey, 1 | 2>>;

/** The one line a warning says: which cap, how full, what to do. */
export function capacityWarnText(key: CapacityKey, used: number, cap: number): string {
  const row = CAPACITY_LABELS.find((r) => r.key === key);
  const name = (row?.label ?? key).toLowerCase();
  const pct = cap > 0 ? Math.floor((used / cap) * 100) : 100;
  const amounts = `${used.toLocaleString()} / ${cap.toLocaleString()} ${row?.unit ?? ''}`.trim();
  return used >= cap
    ? `Your ${name} is full (${amounts}) — make room before you take on more.`
    : `Your ${name} is ${pct}% full (${amounts}) — make room soon.`;
}

/**
 * Which warnings a new snapshot should raise, and the state to keep. Pure: the
 * caller owns the state and the toasts.
 */
export function capacityWarnings(
  capacity: Capacity | undefined,
  state: CapacityWarnState,
): { texts: string[]; next: CapacityWarnState } {
  if (!capacity) return { texts: [], next: state };
  const next: CapacityWarnState = {};
  const texts: string[] = [];
  for (const { key } of CAPACITY_LABELS) {
    if (!capacity.warn.includes(key)) continue; // left the warn list: re-armed
    const { used, cap } = capacity[key];
    const stage: 1 | 2 = used >= cap ? 2 : 1;
    const had = state[key] ?? 0;
    if (stage > had) texts.push(capacityWarnText(key, used, cap));
    next[key] = stage > had ? stage : (had as 1 | 2);
  }
  return { texts, next };
}

/**
 * The §8 held-mail warning (H-1375): what waits in §5.2 hold mail, once per
 * session (the first snapshot after login) and again whenever the hold grows.
 * `seen` is the last summary warned about this session (null at login). Held
 * Shards carry a claim-by day; held items never expire (§5.3).
 */
export function heldMailWarning(
  held: Held | undefined,
  seen: Held | null,
): { text: string | null; next: Held | null } {
  if (!held) return { text: null, next: null };
  const grew =
    !seen || held.mails > seen.mails || held.shards > seen.shards || held.items > seen.items;
  if (!grew) return { text: null, next: held };
  const parts: string[] = [];
  if (held.shards > 0) parts.push(`${held.shards.toLocaleString()} Shards`);
  if (held.items > 0) parts.push(held.items === 1 ? '1 item' : `${held.items} items`);
  const by = held.shardsExpireAt
    ? ` Claim the Shards by ${held.shardsExpireAt.slice(0, 10)} or they expire.`
    : '';
  return {
    text: `Held in your mail because a cap was full: ${parts.join(' and ')}. Make room, then claim.${by}`,
    next: held,
  };
}

/** An item row as the Pack breakdown reads it. */
export type PackItem = { location: string; locationRef: string | null };

/** The Pack bar's count, split by where each row sits (§4: inventory + equipped, per ACCOUNT). */
export interface PackBreakdown {
  /** In the account's bag (shared by every character). */
  carried: number;
  /** Worn by the character being played. */
  worn: number;
  /** Worn by the account's other characters — counted, but on no panel this character opens. */
  wornElsewhere: number;
}

/**
 * Split the account's pack rows (H-1437, `q-pack-gauge-reads-more-than-bag`). The
 * server's `pack.used` counts every inventory + equipped row of the account, so on a
 * multi-character account the bar read 30 while the bag listed 4 and 8 were worn —
 * the other 18 were worn by sibling characters. A split of the server's own reading:
 * when it does not add up to `used` (a stale snapshot), nothing is shown.
 */
export function packBreakdown(
  items: readonly PackItem[],
  characterId: number | null,
  used: number,
): PackBreakdown | null {
  if (characterId === null) return null;
  const self = String(characterId);
  const b: PackBreakdown = { carried: 0, worn: 0, wornElsewhere: 0 };
  for (const it of items) {
    if (it.location === 'inventory') b.carried += 1;
    else if (it.location === 'equipped') {
      if (it.locationRef === self) b.worn += 1;
      else b.wornElsewhere += 1;
    }
  }
  return b.carried + b.worn + b.wornElsewhere === used ? b : null;
}

/** The faint line under the Pack bar; null when the bar already explains itself (bag only). */
export function packBreakdownText(b: PackBreakdown | null): string | null {
  if (!b || (b.worn === 0 && b.wornElsewhere === 0)) return null;
  const parts = [`${b.carried} in the bag`, `${b.worn} worn`];
  if (b.wornElsewhere > 0) parts.push(`${b.wornElsewhere} worn by your other characters`);
  return parts.join(' · ');
}

/** How a refusal names each cap in a sentence ("…overfill your <name>"). */
const REFUSAL_NAMES: Record<CapacityKey, string> = {
  purse: 'coin purse',
  sack: 'sack',
  pack: 'pack',
  chest: 'chest',
  account: 'holdings (every item your account owns, wherever it is)',
};

/**
 * The words for a §5.1 `capacityExceeded` refusal (AD-775 §8: "a refusal names the
 * cap"). Nothing moved, so the line says what to do; `partner` is a direct trade
 * refused at the OTHER side's cap. There is no Shard bank to point at (spec §4.4).
 */
export function capacityRefusalText(cap: CapacityKey | undefined, partner = false): string {
  const name = cap ? REFUSAL_NAMES[cap] : 'carrying space';
  return partner
    ? `That would overfill your trade partner’s ${name} — nothing changed hands.`
    : `That would overfill your ${name} — make room first.`;
}

/** One cap's row on a vendor's capacity shelf (§6.1): where it stands and its next raise. */
export interface CapacityUpgradeRow {
  key: CapacityKey;
  label: string;
  unit: string;
  /** The cap size now. */
  size: number;
  /** The tier the raise would be confirmed at. */
  tier: number;
  /** The raise: the size after it and its full Shard cost. Null at the top of the ladder. */
  next: { cap: number; cost: number } | null;
  /** A pack container the server says this raise may bind (§6.2); it is consumed. */
  container?: { itemId: string; name: string };
  /**
   * The raise is container-gated (§6.2b, AD-778): the server refuses it without a
   * binding, so the row offers **bind** alone and names what opens it.
   */
  containerRequired?: { openers: { id: string; name: string }[] };
}

/**
 * The vendor's capacity rows, read from the snapshot's `capacity.upgrades` (an older
 * server sends none ⇒ no rows). The client prices nothing: every number is the
 * server's.
 */
export function capacityUpgradeRows(capacity: Capacity | undefined): CapacityUpgradeRow[] {
  const offers = capacity?.upgrades;
  if (!capacity || !offers) return [];
  return CAPACITY_LABELS.map(({ key, label, unit }) => ({
    key,
    label,
    unit,
    size: capacity[key].cap,
    tier: offers[key].tier,
    next: offers[key].next,
    ...(offers[key].container ? { container: offers[key].container } : {}),
    ...(offers[key].containerRequired ? { containerRequired: offers[key].containerRequired } : {}),
  }));
}

/** "a Verdant-Hide Satchel", "a Satchel or a Strongbox"; a gate with no named opener says "a container". */
function openerWords(openers: readonly { name: string }[] | undefined): string {
  const names = (openers ?? []).map((o) => `a ${o.name}`);
  if (names.length === 0) return 'a container';
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1] as string}`;
}

/**
 * What a gated row says when no opener is in the pack (§6.2b, §8 — never a silent
 * lock): the raise exists, Shards alone cannot buy it, and which container opens it.
 */
export function capacityGatedText(row: CapacityUpgradeRow): string {
  const next = row.next ? ` to ${row.next.cap.toLocaleString()} ${row.unit}` : '';
  return `Raising your ${row.label.toLowerCase()}${next} needs ${openerWords(row.containerRequired?.openers)} bound to it — Shards alone cannot buy this one.`;
}

/**
 * The confirmation a BINDING asks for (§6.2): the container is used up AND the
 * full cost is still spent — a container unlocks a raise, it never pays for it.
 */
export function capacityBindConfirmText(row: CapacityUpgradeRow): string {
  if (!row.next || !row.container) return capacityUpgradeConfirmText(row);
  return `Bind your ${row.container.name} and spend ◈ ${row.next.cost.toLocaleString()} to raise your ${row.label.toLowerCase()} from ${row.size.toLocaleString()} to ${row.next.cap.toLocaleString()} ${row.unit}? The ${row.container.name} is used up and the Shards are spent for good.`;
}

/** The confirmation a raise asks for (§6.1, §8): the cost, the new cap, and that it is burned. */
export function capacityUpgradeConfirmText(row: CapacityUpgradeRow): string {
  if (!row.next) return `Your ${row.label.toLowerCase()} is as large as it gets.`;
  return `Spend ◈ ${row.next.cost.toLocaleString()} to raise your ${row.label.toLowerCase()} from ${row.size.toLocaleString()} to ${row.next.cap.toLocaleString()} ${row.unit}? The Shards are spent for good.`;
}

/**
 * The words for a refused raise. Nothing was paid in any of them. A
 * `containerRequired` refusal names the cap, the tier and its openers (§6.2b, §8).
 */
export function describeCapacityUpgradeDenial(
  denied: string,
  gate?: { cap: CapacityKey; tier?: number; openers?: readonly { id?: string; name: string }[] },
): string {
  switch (denied) {
    case 'containerRequired': {
      const row = CAPACITY_LABELS.find((r) => r.key === gate?.cap);
      const name = (row?.label ?? 'carrying space').toLowerCase();
      const tier = gate?.tier !== undefined ? ` to tier ${gate.tier}` : '';
      return `Raising your ${name}${tier} needs ${openerWords(gate?.openers)} bound to it — nothing was spent.`;
    }
    case 'topTier':
      return 'That is already as large as it gets.';
    case 'tierChanged':
      return 'That price is out of date — check the new one and confirm again. Nothing was spent.';
    case 'insufficientShards':
      return 'Not enough Shards for that raise — nothing was spent.';
    case 'notAService':
      return 'This trader does not sell carrying space.';
    case 'reputationLocked':
      return 'This trader will not deal with you yet.';
    case 'containerNotFound':
      return 'That container is no longer in your pack — nothing was spent.';
    case 'containerMismatch':
      return 'That container cannot unlock this raise — nothing was spent.';
    default:
      return 'That trader is not here.';
  }
}

/** The line a committed raise says. */
export function capacityUpgradedText(cap: CapacityKey, size: number, shards: number): string {
  const row = CAPACITY_LABELS.find((r) => r.key === cap);
  const label = row?.label ?? cap;
  const unit = row?.unit ?? '';
  return `${label} raised to ${size.toLocaleString()} ${unit} (◈ ${shards.toLocaleString()} spent)`;
}
